/**
 * CROEV Quiz V1 - Quiz Engine
 * Lunavo - Hair Growth Quiz
 *
 * Features:
 * - Multi-step navigation with state management
 * - Single and multi-select question handling
 * - Auto-advance for single-select questions (300ms delay)
 * - Loading animations with timed transitions
 * - Popup modals during loading steps
 * - Dynamic content population based on answers
 * - Hair loss severity scoring (amount, concerns, genetics, stress)
 * - URL parameter generation for redirect
 * - localStorage backup of answers
 * - Shopify theme editor events (section:load, section:unload)
 */

(function() {
  'use strict';

  // Age mapping for calculations
  const AGE_MAP = {
    '20_29': 25,
    '30_39': 35,
    '40_49': 45,
    '50_plus': 55
  };

  // Problem area labels for display (hair concerns)
  const PROBLEM_LABELS = {
    'thinning': 'Thinning Hair',
    'shedding': 'Excessive Shedding',
    'receding': 'Receding Hairline',
    'bald_patches': 'Bald Patches',
    'scalp_issues': 'Scalp Issues',
    'volume': 'Lack of Volume'
  };

  class CroevQuiz {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.redirectUrl = container.dataset.redirectUrl;

      // Get quiz data from JSON
      const dataEl = document.getElementById(`croev-quiz-data-${this.sectionId}`);
      this.config = dataEl ? JSON.parse(dataEl.textContent) : {};

      // State
      this.state = {
        currentStep: 0,
        answers: {},
        history: []
      };

      // DOM elements
      this.steps = Array.from(container.querySelectorAll('.croev-quiz__step'));
      this.progressBar = container.querySelector('.croev-quiz__progress-bar');
      this.progressContainer = container.querySelector('.croev-quiz__progress');

      // Timers
      this.autoAdvanceTimer = null;
      this.checklistTimers = [];

      this.init();
    }

    init() {
      // Clear saved state on fresh page load (start quiz fresh each time)
      this.clearState();

      // Bind events
      this.bindEvents();

      // Show first step
      this.showStep(this.state.currentStep);

      // Update progress
      this.updateProgress();
    }

    bindEvents() {
      // Option selection
      this.container.addEventListener('click', (e) => {
        const option = e.target.closest('.croev-quiz__option');
        if (option) {
          this.handleOptionSelect(option);
        }

        const continueBtn = e.target.closest('[data-action="continue"]');
        if (continueBtn) {
          this.nextStep();
        }

        const backBtn = e.target.closest('[data-action="back"]');
        if (backBtn) {
          this.prevStep();
        }

        const claimBtn = e.target.closest('[data-action="claim-offer"]');
        if (claimBtn) {
          this.handleClaimOffer(claimBtn);
        }

        const skipBtn = e.target.closest('[data-action="skip"]');
        if (skipBtn) {
          this.handleSkip(skipBtn);
        }
      });

      // Keyboard navigation
      this.container.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          const option = e.target.closest('.croev-quiz__option');
          if (option) {
            e.preventDefault();
            this.handleOptionSelect(option);
          }
        }
      });

      // Input field handling
      this.container.addEventListener('input', (e) => {
        const input = e.target.closest('.croev-quiz__input');
        if (input) {
          this.handleInputChange(input);
        }
      });

      // Input field Enter key to continue
      this.container.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          const input = e.target.closest('.croev-quiz__input');
          if (input) {
            const step = input.closest('.croev-quiz__step');
            const continueBtn = step?.querySelector('[data-action="continue"]');
            if (continueBtn && !continueBtn.disabled) {
              this.nextStep();
            }
          }
        }
      });
    }

    // --- Input Handling ---

    handleInputChange(input) {
      const step = input.closest('.croev-quiz__step');
      const questionId = step.dataset.questionId;
      const value = input.value.trim();
      const inputType = input.type;
      const min = input.min ? parseInt(input.min, 10) : null;
      const max = input.max ? parseInt(input.max, 10) : null;
      const required = input.hasAttribute('required');

      // Validate
      let isValid = true;
      const errorEl = step.querySelector('.croev-quiz__input-error');

      if (required && !value) {
        isValid = false;
      } else if (inputType === 'number' && value) {
        const numVal = parseInt(value, 10);
        if (isNaN(numVal)) {
          isValid = false;
        } else if (min !== null && numVal < min) {
          isValid = false;
        } else if (max !== null && numVal > max) {
          isValid = false;
        }
      } else if (inputType === 'email' && value) {
        isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      } else if (inputType === 'date' && value) {
        isValid = !isNaN(Date.parse(value));
      }

      // Update UI
      input.classList.toggle('is-invalid', !isValid && value.length > 0);
      if (errorEl) {
        errorEl.hidden = isValid || value.length === 0;
      }

      // Store answer if valid
      if (isValid && value) {
        this.state.answers[questionId] = { value, label: value };
        this.saveState();
      } else {
        delete this.state.answers[questionId];
      }

      // Enable/disable continue button
      const continueBtn = step.querySelector('[data-action="continue"]');
      if (continueBtn) {
        continueBtn.disabled = !isValid || !value;
      }
    }

    // --- Option Selection ---

    handleOptionSelect(option) {
      const step = option.closest('.croev-quiz__step');
      const questionId = step.dataset.questionId;
      const selectionType = step.dataset.selectionType;
      const autoAdvance = step.dataset.autoAdvance === 'true';
      const value = option.dataset.optionValue;
      const label = option.dataset.optionLabel;

      if (selectionType === 'single') {
        // Deselect all others
        step.querySelectorAll('.croev-quiz__option').forEach(opt => {
          opt.classList.remove('is-selected');
          opt.setAttribute('aria-pressed', 'false');
        });

        // Select this one
        option.classList.add('is-selected');
        option.setAttribute('aria-pressed', 'true');

        // Store answer
        this.state.answers[questionId] = { value, label };
        this.saveState();

        // Auto-advance if enabled
        if (autoAdvance) {
          setTimeout(() => this.nextStep(), 300);
        } else {
          // Enable continue button
          const continueBtn = step.querySelector('[data-action="continue"]');
          if (continueBtn) continueBtn.disabled = false;
        }
      } else {
        // Multi-select toggle
        option.classList.toggle('is-selected');
        const isSelected = option.classList.contains('is-selected');
        option.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

        // Gather all selected values
        const selectedOptions = Array.from(step.querySelectorAll('.croev-quiz__option.is-selected'));
        this.state.answers[questionId] = selectedOptions.map(opt => ({
          value: opt.dataset.optionValue,
          label: opt.dataset.optionLabel
        }));
        this.saveState();

        // Enable/disable continue button based on selection
        const continueBtn = step.querySelector('[data-action="continue"]');
        if (continueBtn) {
          continueBtn.disabled = selectedOptions.length === 0;
        }
      }
    }

    // --- Step Navigation ---

    showStep(index) {
      // Clear any pending timers
      this.clearTimers();

      // Hide all steps
      this.steps.forEach((step, i) => {
        if (i === index) {
          step.classList.add('is-active');
          step.classList.remove('is-exiting');
        } else {
          step.classList.remove('is-active');
        }
      });

      const currentStep = this.steps[index];
      if (!currentStep) return;

      const stepType = currentStep.dataset.stepType;

      // Focus management - only auto-focus input fields, not options/buttons
      setTimeout(() => {
        const inputField = currentStep.querySelector('.croev-quiz__input');
        if (inputField) inputField.focus();
      }, 100);

      // Restore selections for question steps
      if (stepType === 'question_image' || stepType === 'question_text') {
        this.restoreSelections(currentStep);
      }

      // Restore and handle input question steps
      if (stepType === 'question_input') {
        this.restoreInputValue(currentStep);
      }

      // Handle dynamic content for summary steps
      if (stepType === 'summary_profile') {
        this.populateSummaryProfile(currentStep);
      }

      if (stepType === 'summary_results') {
        this.populateSummaryResults(currentStep);
      }

      // Handle loading steps
      if (stepType === 'loading_step') {
        this.handleLoadingStep(currentStep);
      }

      // Handle info steps with dynamic content
      if (stepType === 'info_step') {
        this.populateInfoStep(currentStep);
      }

      // Handle offer step personalization
      if (stepType === 'offer_step') {
        this.populateOfferStep(currentStep);
      }

      // Update progress bar
      this.updateProgress();

      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    restoreSelections(step) {
      const questionId = step.dataset.questionId;
      const answer = this.state.answers[questionId];

      if (!answer) return;

      const isMulti = step.dataset.selectionType === 'multi';
      const values = isMulti ? answer.map(a => a.value) : [answer.value];

      step.querySelectorAll('.croev-quiz__option').forEach(option => {
        const optValue = option.dataset.optionValue;
        if (values.includes(optValue)) {
          option.classList.add('is-selected');
          option.setAttribute('aria-pressed', 'true');
        }
      });

      // Enable continue button if has selection
      const continueBtn = step.querySelector('[data-action="continue"]');
      if (continueBtn && values.length > 0) {
        continueBtn.disabled = false;
      }
    }

    restoreInputValue(step) {
      const questionId = step.dataset.questionId;
      const answer = this.state.answers[questionId];
      const input = step.querySelector('.croev-quiz__input');

      if (!answer || !input) return;

      input.value = answer.value || '';

      // Trigger validation to enable continue button if valid
      if (input.value) {
        this.handleInputChange(input);
      }
    }

    nextStep() {
      if (this.state.currentStep < this.steps.length - 1) {
        this.state.history.push(this.state.currentStep);
        this.state.currentStep++;
        this.showStep(this.state.currentStep);
        this.updateProgress();
        this.saveState();
      }
    }

    prevStep() {
      if (this.state.history.length > 0) {
        this.state.currentStep = this.state.history.pop();
        this.showStep(this.state.currentStep);
        this.updateProgress();
        this.saveState();
      }
    }

    updateProgress() {
      // Always re-query to ensure we have the element
      const bar = this.container.querySelector('.croev-quiz__progress-bar')
        || document.querySelector('.croev-quiz__progress-bar');
      const wrapper = this.container.querySelector('.croev-quiz__progress')
        || document.querySelector('.croev-quiz__progress');
      const pct = this.container.querySelector('.croev-quiz__progress-pct')
        || document.querySelector('.croev-quiz__progress-pct');

      if (!bar) return;

      const progress = (this.state.currentStep / Math.max(this.steps.length - 1, 1)) * 100;
      bar.style.setProperty('width', `${progress}%`, 'important');

      if (wrapper) {
        wrapper.setAttribute('aria-valuenow', Math.round(progress));
      }

      if (pct) {
        pct.textContent = `${Math.round(progress)}%`;
      }
    }

    // --- Dynamic Content Population ---

    getProblemAreasText(limit = 2) {
      const problemAreas = this.state.answers.problem_areas;
      if (!problemAreas || !Array.isArray(problemAreas)) return 'your concerns';

      const labels = problemAreas.slice(0, limit).map(p => PROBLEM_LABELS[p.value] || p.label);
      return labels.join(' & ');
    }

    getProblemAreasTextBold(limit = 3) {
      const problemAreas = this.state.answers.problem_areas;
      if (!problemAreas || !Array.isArray(problemAreas)) return '<strong>your concerns</strong>';

      const labels = problemAreas.slice(0, limit).map(p => PROBLEM_LABELS[p.value] || p.label);
      return '<strong>' + labels.join(' & ') + '</strong>';
    }

    getDatePlus(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }

    // --- Severity Calculation (Hair Loss) ---

    getSeverityData() {
      const hairLossAmount = this.state.answers.hair_loss_amount;
      const problemAreas = this.state.answers.problem_areas;
      const familyHistory = this.state.answers.family_history;
      const stress = this.state.answers.stress;

      // Base score from hair_loss_amount
      let score = 0;
      if (hairLossAmount) {
        const amountMap = { 'a_lot': 3, 'moderate': 2, 'a_little': 1, 'not_yet': 0 };
        score = amountMap[hairLossAmount.value] || 1;
      }

      // +1 per concern selected beyond 2
      if (problemAreas && Array.isArray(problemAreas)) {
        score += Math.max(0, problemAreas.length - 2);
      }

      // +1 if family_history = yes
      if (familyHistory && familyHistory.value === 'yes') {
        score += 1;
      }

      // +1 if stress = all_the_time
      if (stress && stress.value === 'all_the_time') {
        score += 1;
      }

      // Map total score to severity
      if (score <= 1) {
        return {
          badge: 'MODERATE',
          progress: 30,
          level: 'Mild',
          trigger: 'Stress-Related',
          improvement: 'Very High',
          impact: 'Minimal'
        };
      } else if (score <= 3) {
        return {
          badge: 'HIGH',
          progress: 55,
          level: 'Moderate',
          trigger: familyHistory && familyHistory.value === 'yes' ? 'DHT Sensitivity' : 'Stress-Related',
          improvement: 'High',
          impact: 'Moderate'
        };
      } else if (score <= 5) {
        return {
          badge: 'HIGH',
          progress: 70,
          level: 'Significant',
          trigger: familyHistory && familyHistory.value === 'yes' ? 'Genetic' : 'DHT Sensitivity',
          improvement: 'Moderate',
          impact: 'Noticeable'
        };
      } else {
        return {
          badge: 'VERY HIGH',
          progress: 85,
          level: 'Severe',
          trigger: 'Genetic',
          improvement: 'Moderate',
          impact: 'Noticeable'
        };
      }
    }

    // --- Summary Profile Population ---

    populateSummaryProfile(step) {
      const problemAreasText = this.getProblemAreasText();
      const severityData = this.getSeverityData();

      // Update badge text
      const badgeEl = step.querySelector('[data-field="badge_text"]');
      if (badgeEl) {
        badgeEl.textContent = severityData.badge;
      }

      // Update level bar tooltip + dot position
      const markerEl = step.querySelector('[data-field="progress_marker"]');
      if (markerEl) {
        markerEl.style.left = `${severityData.progress}%`;
      }

      // Update alert level heading
      const alertLevelEl = step.querySelector('[data-field="alert_level"]');
      if (alertLevelEl) {
        alertLevelEl.textContent = `${severityData.badge} level`;
      }

      // Update alert message
      const alertEl = step.querySelector('.croev-quiz__profile-alert');
      if (alertEl) {
        const template = alertEl.dataset.alertTemplate || '';
        const message = template
          .replace('{severity_level}', severityData.level)
          .replace('{problem_areas}', problemAreasText);
        const alertP = alertEl.querySelector('p');
        if (alertP) alertP.innerHTML = message;
      }

      // Update stat cards
      const triggerEl = step.querySelector('[data-field="trigger_type"]');
      if (triggerEl) {
        triggerEl.textContent = severityData.trigger;
      }

      const triggerCauseEl = step.querySelector('[data-field="trigger_cause"]');
      if (triggerCauseEl) {
        triggerCauseEl.textContent = severityData.trigger;
      }

      const improvementEl = step.querySelector('[data-field="improvement_level"]');
      if (improvementEl) {
        improvementEl.textContent = severityData.improvement;
      }

      const impactEl = step.querySelector('[data-field="appearance_impact"]');
      if (impactEl) {
        impactEl.textContent = severityData.impact;
      }
    }

    // --- Summary Results Population ---

    populateSummaryResults(step) {
      const date30 = this.getDatePlus(30);
      const date60 = this.getDatePlus(60);

      step.querySelectorAll('[data-template]').forEach(el => {
        const template = el.dataset.template;
        const content = template
          .replace('{date_30}', date30)
          .replace('{date_60}', date60);
        el.innerHTML = content;
      });
    }

    // --- Info Step Population ---

    populateInfoStep(step) {
      // Handle dynamic subheadline with {problem_areas} placeholder
      const subheadlineEl = step.querySelector('.croev-quiz__subheadline--dynamic');
      if (subheadlineEl) {
        const template = subheadlineEl.dataset.subheadlineTemplate || subheadlineEl.textContent;
        const problemAreasText = this.getProblemAreasTextBold();
        const content = template.replace('{problem_areas}', problemAreasText);
        subheadlineEl.innerHTML = content;
      }

      // Duplicate carousel images for seamless infinite scroll
      const carousel = step.querySelector('.croev-quiz__image-carousel');
      if (carousel && !carousel.dataset.duplicated) {
        const track = carousel.querySelector('.croev-quiz__image-carousel-track');
        if (track) {
          const items = track.innerHTML;
          track.innerHTML = items + items; // Duplicate for seamless loop
          carousel.dataset.duplicated = 'true';
        }
      }

      // Arrow navigation for image carousel (desktop)
      if (carousel) {
        const track = carousel.querySelector('.croev-quiz__image-carousel-track');
        const prevBtn = carousel.querySelector('[data-carousel-arrow="prev"]');
        const nextBtn = carousel.querySelector('[data-carousel-arrow="next"]');
        if (track && prevBtn && nextBtn) {
          const scrollAmount = () => {
            const item = track.querySelector('.croev-quiz__image-carousel-item');
            return item ? item.offsetWidth + 12 : 300;
          };
          prevBtn.addEventListener('click', () => {
            track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
          });
          nextBtn.addEventListener('click', () => {
            track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
          });
        }
      }
    }

    // --- Offer Step Population ---

    populateOfferStep(step) {
      const answers = this.state.answers;

      // Personalized subheadline
      const personalizedEl = step.querySelector('[data-offer-personalized]');
      if (personalizedEl) {
        const concerns = this.getProblemAreasTextBold(3);
        const severityData = this.getSeverityData();
        let text = `Based on your <strong>${severityData.level}</strong> hair loss with ${concerns}, we recommend Lunavo\u00AE Renewal Shampoo.`;
        personalizedEl.innerHTML = text;
      }

      // Personalized benefits
      const benefitsEl = step.querySelector('[data-offer-benefits]');
      if (benefitsEl) {
        const checkSvg = '<svg class="croev-quiz__offer-benefit-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>';

        // Map concerns to specific benefits
        const benefitMap = {
          'thinning': 'Targets <strong>thinning hair</strong> at the root by blocking DHT naturally',
          'shedding': 'Reduces <strong>excessive shedding</strong> by strengthening hair follicles',
          'receding': 'Stimulates growth along your <strong>receding hairline</strong> with rosemary oil',
          'bald_patches': 'Reactivates dormant follicles in <strong>thinning patches</strong>',
          'scalp_issues': 'Soothes and heals <strong>scalp irritation</strong> while promoting growth',
          'volume': 'Restores <strong>natural volume</strong> and thickness from the first wash'
        };

        // Default benefits that always show
        const defaultBenefits = [
          'Clinically proven <strong>rosemary oil formula</strong> \u2014 as effective as minoxidil',
          'Simple <strong>2-minute daily routine</strong> \u2014 just shampoo and go',
          '<strong>90-day money-back guarantee</strong> \u2014 zero risk to try'
        ];

        let benefits = [];

        // Add concern-specific benefits
        const problemAreas = answers.problem_areas;
        if (problemAreas && Array.isArray(problemAreas)) {
          problemAreas.slice(0, 3).forEach(p => {
            const benefit = benefitMap[p.value];
            if (benefit) benefits.push(benefit);
          });
        }

        // Add time-based benefit
        const dailyTime = answers.daily_time;
        if (dailyTime && dailyTime.value === 'under_2min') {
          benefits.push('Takes <strong>less than 2 minutes</strong> \u2014 fits your busy routine');
        }

        // Fill with defaults if we don't have enough
        if (benefits.length < 3) {
          defaultBenefits.forEach(b => {
            if (benefits.length < 5 && !benefits.includes(b)) benefits.push(b);
          });
        }

        // Cap at 5
        benefits = benefits.slice(0, 5);

        let html = '<p class="croev-quiz__offer-benefits-title">Why this is perfect for you</p>';
        benefits.forEach(b => {
          html += `<div class="croev-quiz__offer-benefit">${checkSvg}<p class="croev-quiz__offer-benefit-text">${b}</p></div>`;
        });

        benefitsEl.innerHTML = html;
      }
    }

    // --- Loading Step Handling ---

    handleLoadingStep(step) {
      const duration = parseInt(step.dataset.duration, 10) || 3000;
      const hasPopups = step.dataset.hasPopups === 'true';
      const checklist = step.querySelector('.croev-quiz__checklist');

      // Handle checklist animation
      if (checklist) {
        const items = Array.from(checklist.querySelectorAll('.croev-quiz__checklist-item'));
        items.forEach((item, i) => {
          const delay = parseInt(item.dataset.delay, 10) || i * 1000;
          const timer = setTimeout(() => {
            item.classList.add('is-visible');
            setTimeout(() => item.classList.add('is-complete'), 500);
          }, delay);
          this.checklistTimers.push(timer);
        });
      }

      // Handle popup modals during loading
      if (hasPopups) {
        const popups = Array.from(step.querySelectorAll('[data-popup-index]'));
        if (popups.length > 0) {
          this.handleLoadingWithPopups(step, duration, popups);
          return; // Don't set regular auto-advance
        }
      }

      // Regular auto-advance (no popups)
      this.autoAdvanceTimer = setTimeout(() => this.nextStep(), duration);
    }

    handleLoadingWithPopups(step, totalDuration, popups) {
      // Sort popups by trigger percentage
      popups.sort((a, b) => {
        return parseInt(a.dataset.popupTrigger, 10) - parseInt(b.dataset.popupTrigger, 10);
      });

      let currentPopupIndex = 0;
      let elapsedBeforePause = 0;
      let segmentStart = Date.now();
      const progressBar = step.querySelector('.croev-quiz__loading-progress-bar');

      const getElapsed = () => elapsedBeforePause + (Date.now() - segmentStart);

      const scheduleNext = () => {
        if (currentPopupIndex >= popups.length) {
          // All popups shown, advance after remaining time
          const remaining = totalDuration - getElapsed();
          this.autoAdvanceTimer = setTimeout(() => this.nextStep(), Math.max(remaining, 500));
          return;
        }

        const popup = popups[currentPopupIndex];
        const triggerPercent = parseInt(popup.dataset.popupTrigger, 10) / 100;
        const triggerTime = totalDuration * triggerPercent;
        const timeUntilTrigger = triggerTime - getElapsed();

        this.autoAdvanceTimer = setTimeout(() => {
          // Pause: record elapsed time so far
          elapsedBeforePause = getElapsed();

          // Show popup
          popup.hidden = false;

          // Pause progress bar animation
          if (progressBar) progressBar.style.animationPlayState = 'paused';

          // Listen for popup answer
          const handleAnswer = (e) => {
            const btn = e.target.closest('[data-popup-answer]');
            if (!btn) return;

            const answer = btn.dataset.popupAnswer;
            // Store popup answer
            this.state.answers[`popup_${currentPopupIndex}`] = { value: answer, label: answer };

            // Hide popup
            popup.hidden = true;

            // Resume: reset segment start so pause time is excluded
            segmentStart = Date.now();

            // Resume progress bar
            if (progressBar) progressBar.style.animationPlayState = 'running';

            // Clean up listener
            popup.removeEventListener('click', handleAnswer);

            // Schedule next popup or advance
            currentPopupIndex++;
            scheduleNext();
          };

          popup.addEventListener('click', handleAnswer);
        }, Math.max(timeUntilTrigger, 100));
      };

      scheduleNext();
    }

    // --- Timer Management ---

    clearTimers() {
      if (this.autoAdvanceTimer) {
        clearTimeout(this.autoAdvanceTimer);
        this.autoAdvanceTimer = null;
      }
      this.checklistTimers.forEach(t => clearTimeout(t));
      this.checklistTimers = [];
    }

    // --- Skip Handling ---

    handleSkip(btn) {
      // Skip this step without saving an answer
      const step = btn.closest('.croev-quiz__step');
      const questionId = step?.dataset.questionId;

      // Clear any answer for this question
      if (questionId && this.state.answers[questionId]) {
        delete this.state.answers[questionId];
        this.saveState();
      }

      // Move to next step
      this.nextStep();
    }

    // --- Offer Handling ---

    handleClaimOffer(btn) {
      const productUrl = btn.dataset.productUrl;
      const variantId = btn.dataset.variantId;
      const step = btn.closest('.croev-quiz__step');
      const discountCode = step?.dataset.discountCode;

      // Build URL with quiz answers as parameters
      const params = new URLSearchParams();

      // Add all answers
      Object.entries(this.state.answers).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          params.set(key, value.map(v => v.value).join(','));
        } else if (value && value.value) {
          params.set(key, value.value);
        }
      });

      // Add variant if available
      if (variantId) {
        params.set('variant', variantId);
      }

      // Add discount code if available
      if (discountCode) {
        params.set('discount', discountCode);
      }

      // Redirect
      let url = this.redirectUrl || productUrl;
      if (url) {
        const separator = url.includes('?') ? '&' : '?';
        window.location.href = `${url}${separator}${params.toString()}`;
      }
    }

    // --- State Persistence ---

    saveState() {
      try {
        localStorage.setItem(`croev-quiz-${this.sectionId}`, JSON.stringify(this.state));
      } catch (e) {
        console.warn('Could not save quiz state:', e);
      }
    }

    loadState() {
      try {
        const saved = localStorage.getItem(`croev-quiz-${this.sectionId}`);
        if (saved) {
          const parsed = JSON.parse(saved);
          // Only restore answers, start from beginning
          this.state.answers = parsed.answers || {};
        }
      } catch (e) {
        console.warn('Could not load quiz state:', e);
      }
    }

    clearState() {
      try {
        localStorage.removeItem(`croev-quiz-${this.sectionId}`);
      } catch (e) {
        console.warn('Could not clear quiz state:', e);
      }
    }

    // --- Utilities ---

    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }

    destroy() {
      this.clearTimers();
    }
  }

  // Initialize all quiz instances
  function initQuizzes() {
    document.querySelectorAll('[data-section-type="croev-quiz"]').forEach(container => {
      if (!container.croevQuiz) {
        container.croevQuiz = new CroevQuiz(container);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuizzes);
  } else {
    initQuizzes();
  }

  // Handle Shopify theme editor events
  document.addEventListener('shopify:section:load', (e) => {
    const container = e.target.querySelector('[data-section-type="croev-quiz"]');
    if (container) {
      container.croevQuiz = new CroevQuiz(container);
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const container = e.target.querySelector('[data-section-type="croev-quiz"]');
    if (container && container.croevQuiz) {
      container.croevQuiz.destroy();
    }
  });

})();
